// @ts-check
// Página de índice de um objeto (doc 06, seção 2; doc 07, 7.3): cabeçalho com ícone, título,
// "+ Novo" e menu de comandos; barra de view; tabela ou kanban conforme a view.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { searchParamsState } from '../../app/router.js';
import { getIndexView, getViewsForObject, objectsByNamePlural } from '../../data/metadata-store.js';
import { insertRecordLocally } from '../../data/record-store.js';
import { recordImportCounterState } from '../spreadsheet-import/import-model.js';
import { isFieldDisplayable } from '../../lib/field-metadata.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { ViewBar } from '../views/view-bar.js';
import { activeRecordIndexState, applyViewUrlParams, clearViewDrilldown, getViewDrilldown, isDeletedFilterActive } from '../views/view-state.js';
import { drilldownModeOf } from '../../lib/drilldown-links.js';
import { enqueueSnackbar } from '../../ui/snackbar.js';
import { drilldownRefreshState } from '../views/drilldown-chip.js';
import { RecordTable } from './record-table.js';
import { RecordBoard } from './record-board.js';
import { RecordCalendar } from './record-calendar.js';
import { Button, IconButton } from '../../ui/button.js';
import { NotFoundPage } from '../../app/not-found-page.js';
import { openCreateRecordPanel, toggleCommandMenu } from '../side-panel/side-panel-state.js';
import { formatShortcut } from '../../lib/utils.js';
import { canUpdateObjectRecords } from '../../lib/permissions.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { PinnedListCommands } from '../command-menu/pinned-list-commands.js';

/**
 * View sintética para objetos sem view semeada (todas as colunas de negócio).
 * @param {import('../../lib/field-metadata.js').ObjectMetadata} objectMetadata
 * @returns {import('../../data/metadata-store.js').View}
 */
function buildFallbackView(objectMetadata) {
  return {
    id: `fallback-${objectMetadata.id}`,
    objectMetadataId: objectMetadata.id,
    name: t('views.allRecords', { object: translateMetadataLabel(objectMetadata.labelPlural) }),
    type: 'TABLE',
    key: 'INDEX',
    icon: 'IconList',
    viewFields: objectMetadata.fields
      .filter((field) => isFieldDisplayable(field) && !field.isSystem)
      .map((field, index) => ({ id: `fallback-${field.id}`, fieldMetadataId: field.id, position: index, isVisible: index < 8, size: 150 })),
    viewFilters: [],
    viewSorts: [],
    viewGroups: [],
  };
}

/**
 * @param {{ objectNamePlural: string }} props
 */
export function RecordIndexPage({ objectNamePlural }) {
  const objectMetadata = objectsByNamePlural.value.get(objectNamePlural);
  const [totalCount, setTotalCount] = useState(/** @type {number | null} */ (null));
  const [selectedCount, setSelectedCount] = useState(0);
  const [hiddenCount, setHiddenCount] = useState(/** @type {number | null} */ (null));
  const viewId = searchParamsState.value.get('viewId');
  const view = objectMetadata
    ? getViewsForObject(objectMetadata.id).find((candidate) => candidate.id === viewId) ?? getIndexView(objectMetadata.id) ?? buildFallbackView(objectMetadata)
    : null;

  useEffect(() => {
    setTotalCount(null);
    setSelectedCount(0);
    setHiddenCount(null);
  }, [objectMetadata?.id, view?.id]);

  // o menu de comandos oferece "Ver excluídos" para a view aberta
  useEffect(() => {
    activeRecordIndexState.value = objectMetadata && view ? { objectMetadata, view } : null;
    return () => {
      activeRecordIndexState.value = null;
    };
  }, [objectMetadata, view]);

  useEffect(() => {
    if (objectMetadata && view) {
      document.title = `${translateMetadataLabel(view.name)} - ${translateMetadataLabel(objectMetadata.labelPlural)}`;
    }
  }, [objectMetadata?.id, view?.id, view?.name]);

  if (!objectMetadata || !view) return html`<${NotFoundPage} />`;
  // link com filtros, ordenações ou busca (doc 06, seção 14): vira o rascunho da view antes de a lista buscar
  applyViewUrlParams(view, searchParamsState.value.toString() ? `?${searchParamsState.value.toString()}` : '');
  // depois de uma importação a lista é montada de novo com os registros que entraram; "Atualizar" no chip do painel também
  const listKey = `${view.id}:${recordImportCounterState.value}:${drilldownRefreshState.value}`;

  const requestCreate = (/** @type {Record<string, unknown>} */ prefill = {}) =>
    openCreateRecordPanel({
      objectNameSingular: objectMetadata.nameSingular,
      prefill,
      onCreated: (record) => {
        // lista do painel por snapshot: o registro novo fica fora do recorte (os ids são os do clique); avisa e oferece
        // "Limpar" para vê-lo, em vez de somá-lo à lista e o chip culpar "os dados mudaram"
        if (drilldownModeOf(getViewDrilldown(view)) === 'SNAPSHOT') {
          enqueueSnackbar({
            message: t('dashboardDrilldown.createdOutside'), variant: 'info', dedupeKey: 'drilldown-created-outside',
            buttonLabel: t('dashboardDrilldown.chip.clear'), onButtonClick: () => clearViewDrilldown(view),
          });
          return;
        }
        insertRecordLocally(objectMetadata.nameSingular, record, { atStart: true });
        setTotalCount((count) => (count === null ? count : count + 1));
      },
    });

  return html`<${PageCard}
    header=${html`<${PageHeader}
      icon=${objectMetadata.icon ?? 'IconCube'}
      title=${selectedCount > 0
        ? html`${translateMetadataLabel(objectMetadata.labelPlural)}<span class="page-header-title-selection">→ ${t('table.selectedCount', { count: selectedCount })}</span>`
        : translateMetadataLabel(objectMetadata.labelPlural)}
      actions=${html`
        <${PinnedListCommands} objectMetadata=${objectMetadata} view=${view} />
        ${canUpdateObjectRecords(objectMetadata) ? html`<${Button} icon="IconPlus" title=${t('recordIndex.new')} size="small" accent="blue" onClick=${() => requestCreate()} />` : null}
        <${IconButton} icon="IconDotsVertical" size="small" ariaLabel=${`${t('commandMenu.title')} (${formatShortcut('K')})`} onClick=${toggleCommandMenu} />
      `}
    />`}
  >
    <${ViewBar} objectMetadata=${objectMetadata} view=${view} totalCount=${totalCount} hiddenCount=${hiddenCount} />
    ${isDeletedFilterActive(view)
      ? html`<${RecordTable} key=${`${listKey}:trash`} objectMetadata=${objectMetadata} view=${view} onTotalCountChange=${setTotalCount} onRequestCreate=${() => requestCreate()} onSelectionChange=${setSelectedCount} />`
      : view.type === 'CALENDAR'
      ? html`<${RecordCalendar} key=${listKey} objectMetadata=${objectMetadata} view=${view} onTotalCountChange=${setTotalCount} onRequestCreate=${requestCreate} />`
      : view.type === 'KANBAN'
      ? html`<${RecordBoard} key=${listKey} objectMetadata=${objectMetadata} view=${view} onTotalCountChange=${setTotalCount} onRequestCreate=${requestCreate}
          onHiddenCountChange=${setHiddenCount} />`
      : html`<${RecordTable} key=${listKey} objectMetadata=${objectMetadata} view=${view} onTotalCountChange=${setTotalCount} onRequestCreate=${(/** @type {Record<string, unknown> | undefined} */ prefill) => requestCreate(prefill ?? {})} onSelectionChange=${setSelectedCount} />`}
  <//>`;
}
